import { TZDate } from '@date-fns/tz';
import { AddressRecord, DeliveryWindowOutputRecord } from '@navio/nsdk';
import { ApiErrorPage, ApiErrorPageInternal, NoddiSkeleton, useIsWithinTailWindBreakpoint } from 'noddi-ui-web';
import { ErrorPage } from '../ErrorPages/ErrorPage';
import { OutsideServiceAreaInfo } from './OutsideServiceAreaInfo';

import { noddiAsync, URLKeys } from 'noddi-async';
import { SelectedSalesItemsCarGrouped } from 'noddi-types';
import { ReactNode } from 'react';
import { BookingTimeWindowPickerContent } from './BookingTimeWindowPickerContent';
import { isOutsideServiceAreaError } from './isOutsideServiceAreaError';
import { TimeWindowGridSkeleton } from './TimeWindowGridSkeleton';
import { TimeWindowPickerFrame } from './TimeWindowPickerFrame';
import { BookingTimeWindowPickerTranslations } from './types';
import { TimeWindowPickerSurface } from './utils';

type CommonProps = {
  address: AddressRecord;
  cars: SelectedSalesItemsCarGrouped[];
  selectedDeliveryWindow?: { startsAt: string; endsAt: string };
  translations: BookingTimeWindowPickerTranslations;
  onAvailableSelect: (bookingTimeWindow: DeliveryWindowOutputRecord) => void;
  onUnavailableSelect?: (bookingTimeWindow: DeliveryWindowOutputRecord) => void;
  customButton?: ReactNode;
  isInternalApp?: boolean;
  brandDomain: string;
  surface?: TimeWindowPickerSurface;
};

export const BookingTimeWindowPicker = ({
  address,
  cars,
  selectedDeliveryWindow,
  onAvailableSelect,
  onUnavailableSelect,
  translations,
  initialFromDate,
  customButton,
  isInternalApp,
  brandDomain,
  surface = 'card'
}: CommonProps & { initialFromDate?: string }) => {
  const showsDayTabs = useIsWithinTailWindBreakpoint('md');
  const {
    data: latestDeliveryWindow,
    isPending: isLatestDeliveryWindowPending,
    error: latestDeliveryWindowError
  } = noddiAsync.useGet({
    type: URLKeys.getLatestDeliveryWindowIgnoringImpersonation,
    input: { brandDomain }
  });

  const {
    data: firstAvailableDeliveryWindow,
    isPending: isFirstAvailableDeliveryWindowPending,
    error: firstAvailableDeliveryWindowError
  } = noddiAsync.usePostAsGetQuery({
    type: URLKeys.getFirstAvailableDeliveryWindowIgnoringImpersonation,
    input: {
      addressId: address.id,
      cars,
      brandDomain
    }
  });

  // The loaded picker's shell and controls in placeholder form, with the day grid's own skeleton under them, so
  // the first load goes skeleton -> grid without a spinner or a shift once the controls arrive.
  if (isLatestDeliveryWindowPending || isFirstAvailableDeliveryWindowPending) {
    const isOnPage = surface === 'page' && showsDayTabs;
    return (
      <TimeWindowPickerFrame isOnPage={isOnPage}>
        <div className='flex items-center justify-between gap-2'>
          {customButton}
          <NoddiSkeleton className='ml-auto h-9 w-32 md:h-8' />
        </div>
        <div className='flex items-center justify-between gap-3'>
          <NoddiSkeleton className='h-9 w-24 shrink-0 max-[359px]:w-9 md:h-8' />
          <NoddiSkeleton className='h-5 w-28' />
          <NoddiSkeleton className='h-9 w-24 shrink-0 max-[359px]:w-9 md:h-8' />
        </div>
        <TimeWindowGridSkeleton
          layout={showsDayTabs ? 'tabs' : 'columns'}
          isOnPage={isOnPage}
          loadingLabel={translations.loading}
        />
      </TimeWindowPickerFrame>
    );
  }

  if (latestDeliveryWindowError || firstAvailableDeliveryWindowError) {
    const errors = [latestDeliveryWindowError, firstAvailableDeliveryWindowError];
    if (isOutsideServiceAreaError(errors)) {
      return <OutsideServiceAreaInfo />;
    }
    return isInternalApp ? <ApiErrorPageInternal error={errors} /> : <ApiErrorPage error={errors} />;
  }

  if (!latestDeliveryWindow.date || !firstAvailableDeliveryWindow.data.date) {
    return (
      <ErrorPage
        title='Delivery Unavailable'
        description='Sorry, we are unable to deliver to this address at time point in time, as no workers are available. Please try again later.'
      />
    );
  }

  const maxDate = new TZDate(latestDeliveryWindow.date, address.timezone.value);
  const firstAvailableDate = new TZDate(firstAvailableDeliveryWindow.data.date, address.timezone.value);
  const firstAvailableReason = firstAvailableDeliveryWindow.data.reason;
  const initialFromDateTZ = initialFromDate ? new TZDate(new Date(initialFromDate), address.timezone.value) : undefined;

  return (
    <BookingTimeWindowPickerContent
      address={address}
      cars={cars}
      brandDomain={brandDomain}
      selectedDeliveryWindow={selectedDeliveryWindow}
      onAvailableSelect={onAvailableSelect}
      onUnavailableSelect={onUnavailableSelect}
      translations={translations}
      maxDate={maxDate}
      firstAvailableDate={firstAvailableDate}
      firstAvailableReason={firstAvailableReason}
      initialFromDateTZ={initialFromDateTZ}
      customButton={customButton}
      isInternalApp={isInternalApp}
      surface={surface}
    />
  );
};
